import {
    type ColorSliderThumbColorConstantType,
    type ColorSliderMeasuresConstantType,
    type ColorSliderStopsConstantType,
    type ColorSliderRectConstantType,
    type ColorSliderTextConstantType,
    type ColorSliderMeasures,
    type ColorSliderChannel,
    type ColorSliderVector,
    type ColorSliderStops,
    type ColorSliderColor,
    type ColorSliderRange,
    type ColorSliderStop,
    type ColorSliderRect
} from "./type";
import {
    getRelativeChroma,
    parseColorString,
    getMaxChroma,
    getLuminance,
    rgbToOklch,
    oklchToHex,
    formatHex
} from "../../helpers/color";
import {
    formatPercent,
    formatNumber
} from "../../helpers/locale";
import {
    getOpaqueColor,
    withAlpha
} from "../../utils/color";
import {
    getHitArea
} from "../../helpers/theme/control";

export const COLOR_SLIDER_CHROMA_MAX = 0.37;

export const COLOR_SLIDER_HUE_STEP = 10;

export const COLOR_SLIDER_HUE_MAX = 360;

export const COLOR_SLIDER_LIGHTNESS_STOPS = 20;

export const COLOR_SLIDER_CHROMA_STOPS = 5;

export const COLOR_SLIDER_FALLBACK_COLOR: ColorSliderColor = {
    hex: "#808080",
    l: 0.6,
    c: 0,
    h: 0
};

const COLOR_SLIDER_ALPHA_STEP = 0.01;

const COLOR_SLIDER_CHROMA_STEP = 0.001;

const COLOR_SLIDER_RELATIVE_STEP = 0.01;

const COLOR_SLIDER_CHROMA_DIGITS = 3;

const COLOR_SLIDER_HALF = 2;

const getColorSliderRelative = (color: ColorSliderColor): number => {
    return getRelativeChroma([
        color.l,
        color.c,
        color.h
    ]);
};

const getColorSliderChroma = ({
    isChromaRelative,
    lightness,
    color,
    hue
}: {
    isChromaRelative: boolean;
    color: ColorSliderColor;
    lightness: number;
    hue: number;
}): number => {
    return isChromaRelative ? getColorSliderRelative(color) * getMaxChroma(lightness, hue) : color.c;
};

export const getColorSliderRange = ({
    isChromaRelative,
    chromaMax,
    channel
}: {
    channel: ColorSliderChannel;
    isChromaRelative: boolean;
    chromaMax: number;
}): ColorSliderRange => {
    if(channel === "hue") {
        return {
            max: COLOR_SLIDER_HUE_MAX,
            step: 1,
            min: 0
        };
    }

    if(channel === "chroma") {
        return {
            step: isChromaRelative ? COLOR_SLIDER_RELATIVE_STEP : COLOR_SLIDER_CHROMA_STEP,
            max: isChromaRelative ? 1 : chromaMax,
            min: 0
        };
    }

    return {
        step: COLOR_SLIDER_ALPHA_STEP,
        max: 1,
        min: 0
    };
};

export const getColorSliderColor = (color: string): ColorSliderColor => {
    const parsed = parseColorString(color);

    if(!parsed) {
        return COLOR_SLIDER_FALLBACK_COLOR;
    }

    const [
        l,
        c,
        h
    ] = rgbToOklch(parsed.rgb);

    return {
        hex: formatHex(parsed.rgb),
        c: c ?? 0,
        l: l ?? 0,
        h: h ?? 0
    };
};

export const getColorSliderAlpha = (color: string): number => {
    return parseColorString(color)?.alpha ?? 1;
};

export const getColorSliderDefaultValue = ({
    isChromaRelative,
    channel,
    color,
    alpha
}: {
    channel: ColorSliderChannel;
    isChromaRelative: boolean;
    color: ColorSliderColor;
    alpha: number;
}): number => {
    if(channel === "hue") {
        return Math.round(color.h) % COLOR_SLIDER_HUE_MAX;
    }

    if(channel === "alpha") {
        return alpha;
    }

    if(channel === "lightness") {
        return color.l;
    }

    return isChromaRelative ? getColorSliderRelative(color) : color.c;
};

export const getColorSliderMeasures = ({
    touchTargets,
    pointerType,
    iconSizes,
    spaces
}: ColorSliderMeasuresConstantType): ColorSliderMeasures => {
    const isCoarse = pointerType === "coarse";

    const thumbSize = isCoarse ? iconSizes.large + spaces.spacingSm : iconSizes.large;

    const trackThickness = isCoarse ? spaces.spacingMd : spaces.spacingMd - spaces.spacingXs;

    return {
        row: getHitArea({
            visualSize: thumbSize,
            pointerType,
            touchTargets
        }).minHeight,
        trackThickness,
        thumbSize
    };
};

export const getColorSliderStops = ({
    isChromaRelative,
    chromaMax,
    channel,
    color
}: ColorSliderStopsConstantType): ColorSliderStops => {
    const stops: Array<ColorSliderStop> = [];

    if(channel === "hue") {
        const count = COLOR_SLIDER_HUE_MAX / COLOR_SLIDER_HUE_STEP;

        for(let index = 0; index <= count; index += 1) {
            const hue = index * COLOR_SLIDER_HUE_STEP;

            stops.push({
                color: oklchToHex(color.l, getColorSliderChroma({
                    lightness: color.l,
                    isChromaRelative,
                    color,
                    hue
                }), hue),
                fraction: index / count,
                opacity: 1
            });
        }

        return {
            limit: null,
            stops
        };
    }

    if(channel === "alpha") {
        return {
            stops: [
                {
                    color: color.hex,
                    fraction: 0,
                    opacity: 0
                },
                {
                    color: color.hex,
                    fraction: 1,
                    opacity: 1
                }
            ],
            limit: null
        };
    }

    if(channel === "lightness") {
        for(let index = 0; index <= COLOR_SLIDER_LIGHTNESS_STOPS; index += 1) {
            const lightness = index / COLOR_SLIDER_LIGHTNESS_STOPS;

            stops.push({
                color: oklchToHex(lightness, getColorSliderChroma({
                    isChromaRelative,
                    hue: color.h,
                    lightness,
                    color
                }), color.h),
                fraction: index / COLOR_SLIDER_LIGHTNESS_STOPS,
                opacity: 1
            });
        }

        return {
            limit: null,
            stops
        };
    }

    const gamutChroma = getMaxChroma(color.l, color.h);

    const limit = isChromaRelative ? gamutChroma : Math.min(gamutChroma, chromaMax);

    const limitFraction = isChromaRelative ? 1 : chromaMax > 0 ? limit / chromaMax : 0;

    for(let index = 0; index <= COLOR_SLIDER_CHROMA_STOPS; index += 1) {
        stops.push({
            color: oklchToHex(color.l, limit * index / COLOR_SLIDER_CHROMA_STOPS, color.h),
            fraction: limitFraction * index / COLOR_SLIDER_CHROMA_STOPS,
            opacity: 1
        });
    }

    return {
        limit: isChromaRelative ? null : limitFraction,
        stops
    };
};

export const getColorSliderThumbColor = ({
    isChromaRelative,
    channel,
    surface,
    color,
    value
}: ColorSliderThumbColorConstantType): string => {
    if(channel === "hue") {
        return oklchToHex(color.l, getColorSliderChroma({
            lightness: color.l,
            isChromaRelative,
            hue: value,
            color
        }), value);
    }

    if(channel === "alpha") {
        return getOpaqueColor({
            color: withAlpha({
                color: color.hex,
                alpha: value
            }),
            surface
        });
    }

    if(channel === "lightness") {
        return oklchToHex(value, getColorSliderChroma({
            lightness: value,
            isChromaRelative,
            hue: color.h,
            color
        }), color.h);
    }

    return oklchToHex(color.l, isChromaRelative ? value * getMaxChroma(color.l, color.h) : value, color.h);
};

export const getColorSliderValueText = ({
    isChromaRelative,
    localize,
    channel,
    locale,
    value
}: ColorSliderTextConstantType): string => {
    if(channel === "hue") {
        return localize("colorSlider-hueValue", [
            formatNumber({
                options: {
                    maximumFractionDigits: 0
                },
                locale,
                value
            })
        ]);
    }

    if(channel === "chroma" && !isChromaRelative) {
        return formatNumber({
            options: {
                maximumFractionDigits: COLOR_SLIDER_CHROMA_DIGITS,
                minimumFractionDigits: COLOR_SLIDER_CHROMA_DIGITS
            },
            locale,
            value
        });
    }

    return formatPercent({
        options: {
            maximumFractionDigits: 0
        },
        locale,
        value
    });
};

export const getColorSliderLightestColor = ({
    first,
    second
}: {
    second: string;
    first: string;
}): string => {
    const firstColor = parseColorString(first);

    const secondColor = parseColorString(second);

    if(!firstColor || !secondColor) {
        return first;
    }

    return getLuminance(secondColor.rgb) > getLuminance(firstColor.rgb) ? second : first;
};

export const getColorSliderVector = ({
    orientation,
    direction
}: {
    orientation: "horizontal" | "vertical";
    direction: "ltr" | "rtl";
}): ColorSliderVector => {
    if(orientation === "vertical") {
        return {
            x1: "0",
            x2: "0",
            y1: "1",
            y2: "0"
        };
    }

    return {
        x1: direction === "rtl" ? "1" : "0",
        x2: direction === "rtl" ? "0" : "1",
        y1: "0",
        y2: "0"
    };
};

export const getColorSliderRect = ({
    orientation,
    direction,
    thickness,
    length,
    extent
}: ColorSliderRectConstantType): ColorSliderRect => {
    if(orientation === "vertical") {
        return {
            y: length - extent,
            width: thickness,
            height: extent,
            x: 0
        };
    }

    return {
        x: direction === "rtl" ? length - extent : 0,
        height: thickness,
        width: extent,
        y: 0
    };
};

export const getColorSliderPosition = ({
    thumbSize,
    fraction,
    length
}: {
    thumbSize: number;
    fraction: number;
    length: number;
}): number => {
    return thumbSize / COLOR_SLIDER_HALF + Math.max(length - thumbSize, 0) * fraction;
};
